import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatError } from "@/components/shared/helpers";
import {
  Download,
  RefreshCw,
  Server,
  Mail,
  CalendarRange,
  Clock,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
} from "lucide-react";

const LEVEL_STYLES = {
  ok: {
    badge: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    icon: CheckCircle2,
    iconClass: "text-emerald-300",
    indicator: "✅",
  },
  warn: {
    badge: "bg-amber-500/15 text-amber-200 border-amber-500/30",
    icon: AlertTriangle,
    iconClass: "text-amber-300",
    indicator: "⚠️",
  },
  error: {
    badge: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    icon: AlertCircle,
    iconClass: "text-rose-300",
    indicator: "🔴",
  },
};

function StatusCard({ testid, title, titleIcon: TitleIcon, level, label, children }) {
  const style = LEVEL_STYLES[level] || LEVEL_STYLES.ok;
  const Icon = style.icon;
  return (
    <div data-testid={testid} className={`rounded-xl border ${style.badge.split(" ")[2]} bg-white/[0.04] p-4 space-y-2`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-slate-100 font-medium">
          <TitleIcon className="h-4 w-4 text-slate-400" />
          {title}
        </div>
        <span
          data-testid={`${testid}-badge`}
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${style.badge}`}
        >
          <Icon className={`h-3.5 w-3.5 ${style.iconClass}`} />
          {style.indicator} {label}
        </span>
      </div>
      <div className="text-xs text-slate-300 leading-relaxed">{children}</div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "jamais";
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function AdminMaintenance() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [mongoBackups, setMongoBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(true);
  const [downloadingFile, setDownloadingFile] = useState(null);

  const loadStatus = async () => {
    try {
      setRefreshing(true);
      const res = await api.get("/admin/maintenance/status");
      setStatus(res.data);
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  const loadMongoBackups = async () => {
    try {
      setLoadingBackups(true);
      const res = await api.get("/admin/maintenance/mongo-backups");
      setMongoBackups(res.data.backups || []);
    } catch (e) {
      toast({ title: "Erreur backups", description: formatError(e), variant: "destructive" });
    } finally {
      setLoadingBackups(false);
    }
  };

  useEffect(() => {
    loadStatus();
    loadMongoBackups();
  }, []);

  const downloadMongoBackup = async (filename) => {
    try {
      setDownloadingFile(filename);
      const res = await api.get(`/admin/maintenance/mongo-backups/${encodeURIComponent(filename)}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/gzip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast({ title: "✅ Téléchargement", description: filename });
    } catch (e) {
      toast({ title: "Téléchargement impossible", description: formatError(e), variant: "destructive" });
    } finally {
      setDownloadingFile(null);
    }
  };

  const downloadBackup = async () => {
    try {
      setDownloading(true);
      const res = await api.get("/admin/maintenance/backup", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      // Nom fixe — pas de date/timestamp côté client (la rotation est inutile, on ne garde que le dernier)
      const filename = "backup_bdm_latest.json";
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast({ title: "✅ Sauvegarde téléchargée", description: filename });
    } catch (e) {
      toast({ title: "Erreur de sauvegarde", description: formatError(e), variant: "destructive" });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="admin-maintenance-section" className="space-y-6">
      {/* Sauvegarde manuelle */}
      <Card data-testid="maintenance-backup-card" className="border-white/10 bg-white/[0.08]">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center gap-2">
            <Server className="h-5 w-5 text-violet-300" />
            Sauvegarde manuelle
          </CardTitle>
          <div className="text-sm text-slate-300">
            Exportez à la demande l&apos;intégralité de vos données métier (propriétaires, logements, séjours, factures,
            demandes, surcoûts, interventions, utilisateurs sans mots de passe) dans un fichier JSON unique. Conservez
            le fichier dans un emplacement sûr (cloud, disque externe).
          </div>
        </CardHeader>
        <CardContent>
          <Button
            data-testid="maintenance-backup-btn"
            onClick={downloadBackup}
            disabled={downloading}
            className="bg-violet-500 text-white hover:bg-violet-400"
          >
            <Download className={`h-4 w-4 mr-2 ${downloading ? "animate-pulse" : ""}`} />
            {downloading ? "Préparation..." : "💾 Télécharger une sauvegarde"}
          </Button>
          <p className="text-xs text-slate-500 mt-2">
            Format JSON UTF-8, structuré par collection. Les mots de passe et hash sont automatiquement exclus.
          </p>
        </CardContent>
      </Card>

      {/* Sauvegardes automatiques mongodump */}
      <Card data-testid="maintenance-mongo-backups-card" className="border-white/10 bg-white/[0.08]">
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle data-testid="maintenance-mongo-backups-title" className="text-slate-100 flex items-center gap-2">
                <Server className="h-5 w-5 text-emerald-300" />
                Sauvegardes automatiques (mongodump)
              </CardTitle>
              <div className="text-sm text-slate-300 mt-1">
                Archives quotidiennes générées chaque nuit à 03h00 par le scheduler. Rotation automatique
                au-delà de 30 jours. Restauration : <code className="bg-white/10 px-1.5 py-0.5 rounded text-[11px]">mongorestore --gzip --archive=fichier.gz</code>.
              </div>
            </div>
            <Button
              data-testid="maintenance-mongo-backups-refresh"
              variant="outline"
              size="sm"
              onClick={loadMongoBackups}
              disabled={loadingBackups}
              className="border-white/10 bg-white/[0.05] text-slate-200 hover:bg-white/10"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loadingBackups ? "animate-spin" : ""}`} />
              Actualiser
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingBackups ? (
            <div className="text-slate-400 py-6 text-center">Chargement...</div>
          ) : mongoBackups.length === 0 ? (
            <div data-testid="maintenance-mongo-backups-empty" className="rounded-xl border border-dashed border-white/10 bg-white/[0.03] py-8 text-center text-sm text-slate-400">
              Aucune sauvegarde disponible — la première sera créée à 03h00 cette nuit.
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-white/10">
              <table data-testid="maintenance-mongo-backups-table" className="w-full text-sm">
                <thead className="bg-white/[0.05] text-xs uppercase tracking-wide text-slate-300">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Fichier</th>
                    <th className="px-3 py-2 text-right">Taille</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {mongoBackups.map((b) => (
                    <tr key={b.filename} data-testid={`maintenance-mongo-backup-row-${b.filename}`} className="hover:bg-white/[0.03]">
                      <td className="px-3 py-2 text-slate-100 font-medium tabular-nums">{b.date}</td>
                      <td className="px-3 py-2 text-slate-300 font-mono text-xs">{b.filename}</td>
                      <td className="px-3 py-2 text-right text-slate-300 tabular-nums">
                        {b.size_kb >= 1024 ? `${(b.size_kb / 1024).toFixed(1)} MB` : `${b.size_kb} KB`}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          data-testid={`maintenance-mongo-backup-download-${b.filename}`}
                          size="sm"
                          variant="outline"
                          onClick={() => downloadMongoBackup(b.filename)}
                          disabled={downloadingFile === b.filename}
                          className="border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                        >
                          <Download className={`h-3.5 w-3.5 mr-1 ${downloadingFile === b.filename ? "animate-pulse" : ""}`} />
                          {downloadingFile === b.filename ? "..." : "Télécharger"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="bg-white/[0.03] px-3 py-2 text-xs text-slate-400">
                {mongoBackups.length} sauvegarde{mongoBackups.length > 1 ? "s" : ""} disponible{mongoBackups.length > 1 ? "s" : ""}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statut des services */}
      <Card data-testid="maintenance-status-card" className="border-white/10 bg-white/[0.08]">
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-slate-100 flex items-center gap-2">
                <Server className="h-5 w-5 text-emerald-300" />
                Statut des services
              </CardTitle>
              <div className="text-sm text-slate-300 mt-1">
                Vue en temps réel de la santé de votre plateforme.
              </div>
            </div>
            <Button
              data-testid="maintenance-refresh-btn"
              variant="outline"
              size="sm"
              onClick={loadStatus}
              disabled={refreshing}
              className="border-white/10 bg-white/[0.05] text-slate-200 hover:bg-white/10"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
              Actualiser
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-slate-400 py-6 text-center">Chargement...</div>
          ) : !status ? (
            <div className="text-slate-400 py-6 text-center">Impossible de récupérer le statut.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <StatusCard
                testid="status-smtp"
                title="SMTP"
                titleIcon={Mail}
                level={status.smtp.level}
                label={status.smtp.label}
              >
                {status.smtp.detail}
              </StatusCard>

              <StatusCard
                testid="status-ical"
                title="Calendriers iCal"
                titleIcon={CalendarRange}
                level={status.ical.level}
                label={status.ical.label}
              >
                {status.ical.active_sources > 0 ? (
                  <>
                    {status.ical.sources_in_error > 0 && (
                      <div className="text-rose-300">
                        {status.ical.sources_in_error} source
                        {status.ical.sources_in_error > 1 ? "s" : ""} en erreur sur {status.ical.active_sources}
                      </div>
                    )}
                    <div>
                      Dernière sync : <strong>{formatDate(status.ical.last_synced_at)}</strong>
                    </div>
                  </>
                ) : (
                  <div>Aucun propriétaire n&apos;a configuré de calendrier iCal pour l&apos;instant.</div>
                )}
              </StatusCard>

              <StatusCard
                testid="status-drafts"
                title="Séjours en attente"
                titleIcon={Clock}
                level={status.drafts.level}
                label={status.drafts.label}
              >
                {status.drafts.count > 0
                  ? "Séjours détectés via iCal mais pas encore confirmés par le propriétaire."
                  : "Tous les séjours iCal détectés ont été confirmés."}
              </StatusCard>
            </div>
          )}

          {status?.checked_at && (
            <div className="text-xs text-slate-500 mt-3 text-right">
              Mesuré le {formatDate(status.checked_at)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
